import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@services/api'

export function useAvailablePackedGoods(search = '') {
  return useQuery({
    queryKey: ['available-packed-goods', search],
    queryFn: async () => {
      const res = await apiClient.get<any>('/sales-orders/packed-goods', {
        params: { search },
      })
      return res
    },
  })
}

export function useSalesOrders(search = '', status = '') {
  return useQuery({
    queryKey: ['sales-orders', search, status],
    queryFn: async () => {
      const res = await apiClient.get<any>('/sales-orders', {
        params: { search, status },
      })
      return res
    },
  })
}

export function useSalesOrderDetail(id?: number) {
  return useQuery({
    queryKey: ['sales-order-detail', id],
    queryFn: async () => {
      const res = await apiClient.get<any>(`/sales-orders/${id}`)
      return res
    },
    enabled: !!id,
  })
}

export function useCreateSalesOrder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: {
      storeId: number
      orderDate: string
      items: Array<{
        packedGoodId: number
        qtyOrdered: number
        ratePerUnit: number
      }>
    }) => {
      const res = await apiClient.post<any>('/sales-orders', data)
      return res
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales-orders'] })
      queryClient.invalidateQueries({ queryKey: ['available-packed-goods'] })
    },
  })
}

export function useUpdateSalesOrderStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, status }: { id: number; status: string }) => {
      const res = await apiClient.patch<any>(`/sales-orders/${id}/status`, { status })
      return res
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales-orders'] })
    },
  })
}

export function useInvoices(search = '', status = '') {
  return useQuery({
    queryKey: ['invoices', search, status],
    queryFn: async () => {
      const res = await apiClient.get<any>('/invoices', {
        params: { search, status },
      })
      return res
    },
  })
}

export function useInvoiceDetail(id?: number) {
  return useQuery({
    queryKey: ['invoice-detail', id],
    queryFn: async () => {
      const res = await apiClient.get<any>(`/invoices/${id}`)
      return res
    },
    enabled: !!id,
  })
}

export function useCreateInvoice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: {
      salesOrderId: number
      invoiceDate: string
    }) => {
      const res = await apiClient.post<any>('/invoices', data)
      return res
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      queryClient.invalidateQueries({ queryKey: ['sales-orders'] })
    },
  })
}

export function useRecordPayment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: {
      invoiceId: number
      amountPaid: number
    }) => {
      const res = await apiClient.post<any>(`/invoices/${data.invoiceId}/payment`, {
        amountPaid: data.amountPaid,
      })
      return res
    },
    onSuccess: (_, data) => {
      queryClient.invalidateQueries({ queryKey: ['invoice-detail', data.invoiceId] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
    },
  })
}

export function useUpdateInvoiceStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, paymentStatus }: { id: number; paymentStatus: string }) => {
      const res = await apiClient.patch<any>(`/invoices/${id}/status`, { paymentStatus })
      return res
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
    },
  })
}
